import PropTypes from "prop-types";
import { useEffect, useMemo } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
// material
import { styled } from "@mui/material/styles";
import { Box, Drawer, Typography } from "@mui/material";

import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
// components
import Logo from "../../components/Logo";
import Scrollbar from "../../components/Scrollbar";
import NavSection from "../../components/NavSection";
import { MHidden } from "../../components/@material-extend";
import sidebarConfig from "./SidebarConfig";
import { useAuth } from "src/hooks/useAuth.hook";
import { teamsSlice } from "src/store/teams/teams.slice";
import { useDispatch } from "react-redux";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { useIsGrandParent } from "src/hooks/useIsGrandParent";
import { FEATURE, COMPANY_TYPE } from "src/util/enums";
import Token from "src/util/Token";
import { isAmikPayoutDomain } from "src/components/charger/charger.constants";

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 250;

const RootStyle = styled("div")(({ theme }) => ({
  [theme.breakpoints.up("lg")]: {
    flexShrink: 0,
    width: DRAWER_WIDTH,
  },
}));

// const AccountStyle = styled("div")(({ theme }) => ({
//   display: "flex",
//   alignItems: "center",
//   padding: theme.spacing(2, 2.5),
//   borderRadius: theme.shape.borderRadiusSm,
//   backgroundColor: theme.palette.grey[200],
// }));

// ----------------------------------------------------------------------

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

const ITEM_HEIGHT = 28;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 100,
    },
  },
};

const notASideBarLink = ["Finance"]    //ARRAY OF FEATUREs NOT TO BE SHOWN AS A SIDEBAR LINK

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar }) {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { teamId, team } = useSelectedTeam();
  // Super admin = user logged in with a Grand Parent (GP) company (from JWT).
  const isGrandParent = useIsGrandParent();
  const isRootAdmin = useMemo(() => {
    const parsed = Token.parsedToken();
    return parsed.success &&
      parsed.data?.companyType === COMPANY_TYPE.GRAND_PARENT &&
      parsed.data?.userType === 'ROOT';
  }, []);
  const company = useMemo(() => {
    // Compare as strings: role.teamId and company.id can differ in type
    // (number vs string), and a strict-equality miss here would drop the
    // company as the default selection and leave it unselected on load.
    return user?.role?.find(
      (team) => String(team.teamId) === String(user?.company?.id)
    );
  }, [user]);
  // "Newly created super admin" = a non-ROOT user inside the Grand Parent company.
  // The super-admin sidebar rules apply ONLY to them. ROOT super admins and every
  // company-level login keep the existing (unfiltered) behavior.
  const isNewSuperAdmin = isGrandParent && !isRootAdmin;

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleChange = (event) => {
    const {
      target: { value },
    } = event;
    dispatch(teamsSlice.actions.setTeamId(+value));
    localStorage.setItem('selectedTeamId', JSON.stringify(value));
  };

  useEffect(() => {
    if (!user?.role?.length) return;

    // Team ids this user may actually select: their team roles plus their own
    // company (the top entry in the switcher).
    const validIds = new Set(
      user.role
        .map((r) => String(r.teamId))
        .concat(user?.company?.id != null ? [String(user.company.id)] : [])
    );

    const selectedFromLS = JSON.parse(localStorage.getItem("selectedTeamId"));
    // Honor the remembered company ONLY if it still belongs to this user. A
    // stale value (a previous login / a different user on this browser) would
    // otherwise be selected, match no role, and leave the whole menu empty.
    const remembered =
      selectedFromLS != null && validIds.has(String(selectedFromLS))
        ? selectedFromLS
        : null;

    // Default selection: remembered -> the user's own company -> first team.
    const next =
      remembered ?? company?.teamId ?? user?.role?.[0]?.teamId ?? null;

    if (next != null) {
      dispatch(teamsSlice.actions.setTeamId(next));
      localStorage.setItem("selectedTeamId", JSON.stringify(next));
    }
  }, [user])

  // useEffect(() => {
  //   team?.teamId ??
  //     dispatch(
  //       teamsSlice.actions.setTeamId(
  //         company?.teamId ?? user?.role?.[0]?.teamId ?? null
  //       )
  //     );
  // }, [dispatch, user, company, team?.teamId]);

  const navConfig = useMemo(() => {
    // Use team data which already has access permissions
    const currentTeam = team;

    // Return empty array if team or team.access is not available
    if (!currentTeam || !currentTeam.access || !Array.isArray(currentTeam.access)) {
      return [];
    }

    // Check if Grand Parent Company is selected (teamId matches user.company.id)
    const isGrandParentCompanySelected = String(teamId) === String(user?.company?.id);

    // Check if user has Finance FA access (grand parent company access)
    const financeAccess = currentTeam.access.find(
      (item) => item.featureName === "Finance"
    );
    const hasFinanceConfigAccess =
      financeAccess?.view === "Y" &&
      financeAccess?.create === "Y" &&
      financeAccess?.update === "Y" &&
      financeAccess?.delete === "Y";

    // Add ChargingSite feature if not available from backend
    const existingChargingSite = currentTeam.access.find(feature => feature.featureName === "ChargingSite");
    let updatedAccess = [...currentTeam.access];

    // if (!existingChargingSite) {
    //   const chargingSiteFeature = {
    //     create: "Y",
    //     delete: "Y",
    //     featureId: updatedAccess.length + 1,
    //     featureName: "ChargingSite",
    //     update: "Y",
    //     view: "Y"
    //   };
    //   updatedAccess = [...updatedAccess, chargingSiteFeature];
    // }

    const config = updatedAccess.reduce((finalConfig, item) => {
      if (item.view === "Y" && item.featureName && !notASideBarLink.includes(item.featureName)) {
        // Super-admin navigation rules apply ONLY to newly created super admins.
        // ROOT super admins and all company-level logins fall straight through to
        // the access-based rendering below, exactly like the existing behavior.
        if (isNewSuperAdmin) {
          // GP-only tabs are hidden unless the GP company itself is selected —
          // except Roles, which a super admin manages inside each child company.
          // Use FEATURE constants so these match the exact featureName the backend
          // sends (e.g. FEATURE.ROLES === "Role", not "Roles").
          const gpOnlyFeatures = [
            FEATURE.PAYMENTGATEWAY,
            FEATURE.ROLES,
            FEATURE.SENDMESSAGES,
            FEATURE.COMPANY,
            FEATURE.CMCUSTOMERS,
          ];
          const isRolesException = item.featureName === FEATURE.ROLES;
          if (
            gpOnlyFeatures.includes(item.featureName) &&
            !isGrandParentCompanySelected &&
            !isRolesException
          ) {
            return finalConfig;
          }

          // Team-level tabs only make sense inside a specific child company, so
          // hide them when the GP company itself is selected.
          const teamOnlyFeatures = [
            FEATURE.CHARGER,
            FEATURE.TAG,
            FEATURE.PRICEGROUP,
            FEATURE.VEHICLE,
            FEATURE.CHARGINGSITE,
            FEATURE.TEAM,
            FEATURE.SETTINGS,
            FEATURE.CUSTOMERS,
          ];
          if (teamOnlyFeatures.includes(item.featureName) && isGrandParentCompanySelected) {
            return finalConfig;
          }

          // EV Drivers is not relevant to the super-admin flow inside a child company.
          if (item.featureName === FEATURE.CUSTOMERS && !isGrandParentCompanySelected) {
            return finalConfig;
          }
        }

        // "Customer" (CM Customers) is a super-admin-only tab. Hide it for every
        // non-super-admin (company / team level) login.
        if (!isGrandParent && item.featureName === FEATURE.CMCUSTOMERS) {
          return finalConfig;
        }

        // "Customer" (CM Customers) is a super-admin-only tab. Hide it for every
        // non-super-admin (company / team level) login.
        if (item.featureName === FEATURE.CMCUSTOMERS && !isGrandParent) {
          return finalConfig;
        }

        // WhatsApp is a super-admin-only feature. Hide it for every non-super-admin
        // (company / team level) login even if the access row somehow carries it.
        if (item.featureName === FEATURE.WHATSAPP && !isGrandParent) {
          return finalConfig;
        }

        // Prevent generating an empty sidebar item if the feature name is not mapped in sidebarConfig
        if (!sidebarConfig[item.featureName]) {
          return finalConfig;
        }

        // One link per destination. The access array can legitimately carry the
        // same feature twice — a feature name seeded under two cms_feature ids,
        // or a user holding two roles in the same company — and rendering one
        // item per access row turned that into a duplicated menu entry (the
        // fixed-order sort below then placed the copies side by side). Every
        // config.push after this reduce already guards the same way.
        if (finalConfig.some((entry) => entry.path === sidebarConfig[item.featureName].path)) {
          return finalConfig;
        }

        finalConfig = [...finalConfig, { ...sidebarConfig[item.featureName], access: item }];
      }
      return finalConfig;
    }, []);

    // Add PaymentGateway menu if user has Finance FA access AND Grand Parent Company is selected
    // even if it's not in the backend access array
    if (hasFinanceConfigAccess && isGrandParentCompanySelected && sidebarConfig[FEATURE.PAYMENTGATEWAY]) {
      const existingPaymentGateway = config.find(
        (item) => item.path === sidebarConfig[FEATURE.PAYMENTGATEWAY].path
      );
      if (!existingPaymentGateway) {
        config.push({
          ...sidebarConfig[FEATURE.PAYMENTGATEWAY],
          access: {
            featureName: "PaymentGateway",
            view: "Y",
            create: "Y",
            update: "Y",
            delete: "Y",
          },
        });
      }
    }

    // Dashboard — always visible to all logged-in users
    if (sidebarConfig.Dashboard) {
      const exists = config.find((item) => item.path === sidebarConfig.Dashboard.path);
      if (!exists) {
        config.push({
          ...sidebarConfig.Dashboard,
          access: { featureName: "Dashboard", view: "Y", create: "Y", update: "Y", delete: "Y" },
        });
      }
    }

    // API Keys — visible only to the ROOT super admin
    if (isRootAdmin && sidebarConfig[FEATURE.APIKEYS]) {
      const exists = config.find((item) => item.path === sidebarConfig[FEATURE.APIKEYS].path);
      if (!exists) {
        config.push({
          ...sidebarConfig[FEATURE.APIKEYS],
          access: { featureName: "ApiKeys", view: "Y", create: "Y", update: "Y", delete: "Y" },
        });
      }
    }

    // Audit Logs — visible only to the ROOT super admin
    if (isRootAdmin && sidebarConfig[FEATURE.AUDITLOGS]) {
      const exists = config.find((item) => item.path === sidebarConfig[FEATURE.AUDITLOGS].path);
      if (!exists) {
        config.push({
          ...sidebarConfig[FEATURE.AUDITLOGS],
          access: { featureName: "AuditLogs", view: "Y", create: "Y", update: "Y", delete: "Y" },
        });
      }
    }

    // System Settings (feature flags, etc.) — visible only to the ROOT super admin
    if (isRootAdmin && sidebarConfig.AdminSettings) {
      const exists = config.find((item) => item.path === sidebarConfig.AdminSettings.path);
      if (!exists) {
        config.push({
          ...sidebarConfig.AdminSettings,
          access: { featureName: "AdminSettings", view: "Y", create: "Y", update: "Y", delete: "Y" },
        });
      }
    }

    // WhatsApp — always shown to super admins (Grand Parent company users), and
    // never at company/team level (the non-GP guard in the reduce above drops it
    // from the access array). Visibility is not tied to the WhatsApp role feature.
    if (isGrandParent && sidebarConfig[FEATURE.WHATSAPP]) {
      const exists = config.find((item) => item.path === sidebarConfig[FEATURE.WHATSAPP].path);
      if (!exists) {
        config.push({
          ...sidebarConfig[FEATURE.WHATSAPP],
          access: { featureName: "WhatsApp", view: "Y", create: "Y", update: "Y", delete: "Y" },
        });
      }
    }

    // EV Drivers — shown to the ROOT super admin, who is unlimited, on top of the
    // company admins whose backend access already includes it.
    //
    // NOT force-fed to a limited super admin: EV drivers belong to a company or
    // one of its teams, none of which a newly created super admin owns. The
    // access-array rules above already withhold the tab from them whichever
    // company is selected, and this unconditional push was overriding that.
    if (isRootAdmin && sidebarConfig[FEATURE.CUSTOMERS]) {
      const exists = config.find(
        (item) => item.path === sidebarConfig[FEATURE.CUSTOMERS].path
      );
      if (!exists) {
        config.push({
          ...sidebarConfig[FEATURE.CUSTOMERS],
          access: {
            featureName: "EvDriver",
            view: "Y",
            create: "Y",
            update: "Y",
            delete: "Y",
          },
        });
      }
    }

    // Payout — unconditional for company-level users on Amik-branded hosts and
    // for the ROOT super admin, who is unlimited by definition.
    //
    // A limited super admin keeps it unless their role explicitly denies it. The
    // distinction matters: a role that carries a Payout row with view "N" is a
    // deliberate denial, while no row at all means the Payout feature does not
    // exist to be granted yet (no cms_feature row, so it is absent from the role
    // editor). Treating that absence as a denial took the tab away from super
    // admins who never had any way of being given it.
    const payoutAccess = updatedAccess.find(
      (item) => item.featureName === FEATURE.PAYOUT
    );
    const payoutExplicitlyDenied = !!payoutAccess && payoutAccess.view !== "Y";
    if (
      ((isAmikPayoutDomain() && !isGrandParent) ||
        isRootAdmin ||
        (isGrandParent && !payoutExplicitlyDenied)) &&
      sidebarConfig[FEATURE.PAYOUT]
    ) {
      const exists = config.find(
        (item) => item.path === sidebarConfig[FEATURE.PAYOUT].path
      );
      if (!exists) {
        config.push({
          ...sidebarConfig[FEATURE.PAYOUT],
          access: {
            featureName: "Payout",
            view: "Y",
            create: "Y",
            update: "Y",
            delete: "Y",
          },
        });
      }
    }

    // Sort sidebar items to match the fixed display order defined in sidebarConfig
    const sidebarOrder = Object.values(sidebarConfig).map((item) => item.path);
    config.sort((a, b) => {
      const indexA = sidebarOrder.indexOf(a.path);
      const indexB = sidebarOrder.indexOf(b.path);
      return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
    });

    return config;
  }, [team, teamId, user, isGrandParent, isRootAdmin, isNewSuperAdmin]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: "100%",
        "& .simplebar-content": {
          height: "100%",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box sx={{ px: 2.5, py: 3 }}>
        <Box component={RouterLink} to="/" sx={{ display: "inline-flex" }}>
          <Logo />
        </Box>
      </Box>

      <Box sx={{ mb: 5, mx: 2.5 }}>
        <FormControl sx={{ m: 1 }}>
          <Select
            style={{ width: "204px", borderRadius: 4, height: "56px" }}
            value={team?.teamId ?? ""}
            onChange={handleChange}
            MenuProps={MenuProps}
          >
            <MenuItem
              key={user?.company?.id}
              value={user?.company?.id}
              sx={{
                fontWeight: 600,
                color: "var(--app-color)",
              }}
              disabled={!company}
            >
              {user?.company?.name ?? ""}
            </MenuItem>
            {user?.role
              ?.filter((team) => team.teamId !== user?.company?.id)
              ?.map((team) => (
                <MenuItem
                  key={team.teamId}
                  value={team.teamId}
                  sx={{
                    pl: 4,
                  }}
                >
                  {team.teamName}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        {/* <Link underline="none" component={RouterLink} to="#">
          <AccountStyle>
            <Avatar src={account.photoURL} alt="photoURL" />
            <Box sx={{ ml: 2 }}>
              <Typography variant="subtitle2" sx={{ color: "text.primary" }}>
                {userDetails.name}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {account.role}
              </Typography>
            </Box>
          </AccountStyle>
        </Link> */}
      </Box>

      <NavSection navConfig={navConfig} />

      <Box sx={{ flexGrow: 1 }} />

      <Box sx={{ px: 2.5, pb: 3, mt: 10 }}>
        {/* <Stack
          alignItems="center"
          spacing={3}
          sx={{
            p: 2.5,
            pt: 5,
            borderRadius: 2,
            position: "relative",
            bgcolor: "grey.200",
          }}
        >
          <Box
            component="img"
            src="/static/illustrations/illustration_avatar.png"
            sx={{ width: 100, position: "absolute", top: -50 }}
          />

          <Box sx={{ textAlign: "center" }}>
            <Typography gutterBottom variant="h6">
              Get more?
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              From only $69
            </Typography>
          </Box>

          <Button
            fullWidth
            href="https://material-ui.com/store/items/minimal-dashboard/"
            target="_blank"
            variant="contained"
          >
            Upgrade to Pro
          </Button>
        </Stack> */}
      </Box>
    </Scrollbar>
  );

  return (
    <RootStyle>
      <MHidden width="lgUp">
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: { width: DRAWER_WIDTH },
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>

      <MHidden width="lgDown">
        <Drawer
          open
          variant="persistent"
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              bgcolor: "background.default",
            },
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>
    </RootStyle>
  );
}
